import React from 'react';
import axiosInstance from '../services/axiosInstance';
import type { ClientConfig } from './types';

type FeatureFlagsContextValue = {
  flags: ClientConfig;
  loading: boolean;
  refresh: () => Promise<void>;
};

const DEFAULT_FLAGS: ClientConfig = {
  generationFromScratchEnabled: false,
  creativeStudioEnabled: false,
  mediaTranslatorEnabled: false,
  extraCreditPurchaseEnabled: false,
};

const FeatureFlagsContext = React.createContext<FeatureFlagsContextValue | undefined>(undefined);

export function FeatureFlagsProvider({ children }: { children: React.ReactNode }) {
  const [flags, setFlags] = React.useState<ClientConfig>(DEFAULT_FLAGS);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/client-config');
      const next: ClientConfig = {
        generationFromScratchEnabled: !!res.data?.generationFromScratchEnabled,
        creativeStudioEnabled: !!res.data?.creativeStudioEnabled,
        mediaTranslatorEnabled: !!res.data?.mediaTranslatorEnabled,
        extraCreditPurchaseEnabled: !!res.data?.extraCreditPurchaseEnabled,
      };
      setFlags(next);
    } catch {
      // Safe default: feature flags off.
      setFlags(DEFAULT_FLAGS);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, refresh }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const ctx = React.useContext(FeatureFlagsContext);
  if (!ctx) throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  return ctx;
}

export function useFeatureFlag<K extends keyof ClientConfig>(flag: K): ClientConfig[K] {
  const { flags } = useFeatureFlags();
  return flags[flag];
}
